/* ==========================================================================
   ux-header — the new-design header.

   Loaded only from sections/ux-header.liquid.

   Every interaction is the theme's own: `hover-disclosure` opens a panel,
   `header-drawer` opens the mobile drawer, `collapsible-elements` runs the
   accordion, and the search popdown, the cart drawer and the account link are
   Broadcast's markup. This element adds the two things none of them has: the
   bar that leaves on scroll down and returns on scroll up, and the wiring from
   our hamburger to `header-drawer`'s `theme:drawer:toggle` event.

   The bar is position: fixed while stuck and the root reserves its height, so
   nothing below it jumps. It never hides while a panel or the drawer is open,
   and the drawer is closed when the viewport crosses the desktop breakpoint —
   below that the drawer is display: none, and its page scroll lock would
   otherwise outlive it.
   ========================================================================== */

/* Must match assets/ux-header.css. */
const UX_HEADER_DESKTOP = 1024;

/* px of travel in one direction before the bar reacts. */
const UX_HEADER_DELTA = 8;

customElements.define(
  'ux-header',
  class extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.toggle = this.querySelector('[data-ux-header-toggle]');
      this.drawer = this.querySelector('header-drawer');
      this.sticky = this.hasAttribute('data-sticky');
      this.offset = 0;
      this.downBy = 0;
      this.upBy = 0;
      this.ticking = false;

      this.onScroll = this.onScroll.bind(this);
      this.frame = this.frame.bind(this);
      this.resize = this.resize.bind(this);
      this.toggleDrawer = this.toggleDrawer.bind(this);

      if (this.toggle && this.drawer) {
        this.toggle.addEventListener('click', this.toggleDrawer);
      }

      window.addEventListener('resize', this.resize, {passive: true});
      document.addEventListener('shopify:section:load', this.resize);

      this.measure();

      if (this.sticky) {
        window.addEventListener('scroll', this.onScroll, {passive: true});
        this.frame();
      }
    }

    disconnectedCallback() {
      if (this.toggle) this.toggle.removeEventListener('click', this.toggleDrawer);

      window.removeEventListener('resize', this.resize);
      window.removeEventListener('scroll', this.onScroll);
      document.removeEventListener('shopify:section:load', this.resize);
    }

    toggleDrawer() {
      this.placeDrawer();
      this.drawer.dispatchEvent(
        new CustomEvent('theme:drawer:toggle', {
          detail: {button: this.toggle},
        })
      );
    }

    resize() {
      if (this.drawer && window.innerWidth >= UX_HEADER_DESKTOP && this.drawer.classList.contains('is-open')) {
        this.drawer.dispatchEvent(new CustomEvent('theme:drawer:close', {bubbles: true}));
      }

      this.measure();
      if (this.drawer?.classList.contains('is-open')) this.placeDrawer();
    }

    /* The drawer hangs from the bar's bottom edge. Until the bar sticks, the
       announcement bar (or whatever sits above the root) pushes it down, so
       the drawer has to start that much lower too. The page is scroll-locked
       while the drawer is open, so one reading holds until it closes. */
    placeDrawer() {
      if (!this.drawer) return;

      const top = this.classList.contains('ux-header--stuck') ? 0 : Math.max(0, this.getBoundingClientRect().top);
      this.style.setProperty('--ux-header-drawer-offset', `${top}px`);
    }

    /* The root is in flow above the bar, so its own offset is where the bar
       would leave the top of the viewport. */
    measure() {
      this.offset = this.getBoundingClientRect().top + window.scrollY;
    }

    onScroll() {
      if (this.ticking) return;

      this.ticking = true;
      window.requestAnimationFrame(this.frame);
    }

    frame() {
      this.ticking = false;

      const y = window.scrollY;

      if (this.lastY === undefined) {
        this.lastY = y;
      }

      /* Direction has to accumulate, or a slow scroll never crosses the
         threshold. */
      const delta = y - this.lastY;
      this.lastY = y;

      if (delta > 0) {
        this.downBy += delta;
        this.upBy = 0;
      } else if (delta < 0) {
        this.upBy -= delta;
        this.downBy = 0;
      }

      if (y <= this.offset) {
        this.classList.remove('ux-header--stuck', 'ux-header--hidden');
        return;
      }

      this.classList.add('ux-header--stuck');

      /* A panel or the drawer is open: the bar stays where it is. */
      if (this.classList.contains('meganav--visible') || this.drawer?.classList.contains('is-open')) {
        this.classList.remove('ux-header--hidden');
        return;
      }

      if (this.downBy > UX_HEADER_DELTA) {
        this.classList.add('ux-header--hidden');
      } else if (this.upBy > UX_HEADER_DELTA) {
        this.classList.remove('ux-header--hidden');
      }
    }
  }
);
